import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { SecureStorageService } from './secure-storage.service';
import { AlertController } from '@ionic/angular';
import { Router } from '@angular/router';
import { normalizeApiText } from 'src/app/utils/text-normalizer';
import { ionicSafeHtml } from 'src/app/utils/ionic-safe-html';

@Injectable({
  providedIn: 'root'
})
export class HttpsService {
  private bannedAlertOpen = false;
  private storageAlertOpen = false;

  constructor(
    public http: HttpClient,
    private secureStorage: SecureStorageService,
    private alertCtrl: AlertController,
    private router: Router
  )
  { }

  async post(body, file) {
    // 1. Recupera o token do SecureStorage
    const token = await this.secureStorage.getItem('user_token');

    // 2. Cria os headers com o Token
    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': token ? `Bearer ${token}` : ''
      })
    };

    try {
      const response: any = normalizeApiText(await this.http.post(environment.apiUrl + file, body, httpOptions).toPromise());
      this.handleBannedResponse(response);
      this.handleStorageFullResponse(response);
      return response;
    } catch (error: any) {
      const errorResponse = normalizeApiText(error?.error);
      this.handleStorageFullResponse(errorResponse);
      this.handleAuthorizationError(error, errorResponse);
      throw error;
    }
  }

  async get(url) {
    const token = await this.secureStorage.getItem('user_token');

    const httpOptions = {
      headers: new HttpHeaders({
        'Authorization': token ? `Bearer ${token}` : ''
      })
    };

    try {
      const response: any = normalizeApiText(await this.http.get(url, httpOptions).toPromise());
      this.handleBannedResponse(response);
      this.handleStorageFullResponse(response);
      return response;
    } catch (error: any) {
      const errorResponse = normalizeApiText(error?.error);
      this.handleStorageFullResponse(errorResponse);
      this.handleAuthorizationError(error, errorResponse);
      throw error;
    }
  }

  async getBlob(file: string): Promise<Blob> {
    const token = await this.secureStorage.getItem('user_token');
    const headers = new HttpHeaders({
      'Authorization': token ? `Bearer ${token}` : ''
    });

    return await this.http.get(environment.apiUrl + file, {
      headers,
      responseType: 'blob'
    }).toPromise() as Blob;
  }

  private async handleBannedResponse(response: any) {
    if (!response?.banned || this.bannedAlertOpen) {
      return;
    }

    this.bannedAlertOpen = true;
    const alert = await this.alertCtrl.create({
      header: 'Conta banida',
      subHeader: 'Acesso bloqueado',
      message: ionicSafeHtml(`
        <div class="ban-alert-content">
          <div class="ban-alert-icon">!</div>
          <p>${response.msg || 'Você feriu as diretrizes do aplicativo e sua conta foi banida por prática ilegal.'}</p>
          <small>Este bloqueio impede o uso do aplicativo e a criação de novas contas.</small>
        </div>
      `),
      cssClass: 'ban-account-alert',
      backdropDismiss: false,
      keyboardClose: false,
      buttons: []
    });

    await alert.present();
  }

  private async handleStorageFullResponse(response: any): Promise<void> {
    const storageResponse = this.findStorageFullResponse(response);
    if (!storageResponse || this.storageAlertOpen) return;

    this.storageAlertOpen = true;
    const alert = await this.alertCtrl.create({
      header: 'Armazenamento esgotado',
      message: ionicSafeHtml(`
        <p>Não há espaço disponível para concluir este envio.</p>
        <p>Seus arquivos atuais continuam preservados. Exclua arquivos que não precisa mais ou escolha um plano com maior capacidade.</p>
      `),
      cssClass: ['custom-alert', 'alert-confirm'],
      buttons: [
        { text: 'Agora não', role: 'cancel' },
        { text: 'Ver armazenamento', handler: () => this.router.navigate(['/armazenamento']) },
        { text: 'Ver planos', handler: () => this.router.navigate(['/planos']) }
      ]
    });

    alert.onDidDismiss().then(() => {
      this.storageAlertOpen = false;
    });

    await alert.present();
  }

  private findStorageFullResponse(value: any): any | null {
    if (!value || typeof value !== 'object') return null;
    if (value.storage_full === true || value.storage_full === 1 || value.storage_full === '1') return value;

    for (const item of Object.values(value)) {
      const found = this.findStorageFullResponse(item);
      if (found) return found;
    }
    return null;
  }

  private handleAuthorizationError(error: any, response: any): void {
    const status = Number(error?.status || 0);
    const code = String(response?.code || '').trim().toUpperCase();

    if (status === 401 || ['NAO_AUTENTICADO', 'SESSAO_INVALIDA'].includes(code)) {
      window.dispatchEvent(new CustomEvent('authorization-invalid'));
      return;
    }

    if (status === 403 || response?.permission_denied === true || code === 'PERMISSAO_NEGADA') {
      window.dispatchEvent(new CustomEvent('authorization-refresh-needed'));
    }
  }

}
